import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import VehicleSelector from "./VehicleSelector";
import QuantityControl from "./QuantityControl";
import { getPickupMinLeadError } from "./DateSelectModal";
import { useVehicles } from "../../hooks/VehicleContext";
import {
  getBookingQuantityLimits,
  clampQuantities,
} from "../../utils/vehicleBookingLimits";
import useGooglePlaces, {
  getPlaceFullAddress,
  placeFromStored,
  placeToStored,
} from "../../hooks/useGooglePlaces";
import PlaceSuggestionLabel from "./PlaceSuggestionLabel";
import axios from "axios";
import { toast } from "../../utils/toast";
import {
  validatePickupServiceArea,
  isOutsideServiceAreaMessage,
  hasResolvedPlaceCoords,
} from "../../utils/validatePickupServiceArea";
import {
  getStopRouteValidationError,
  validateStopsOnRoute,
} from "../../utils/validateStopOnRoute";
import {
  BOOKING_DRAFT_STORAGE_KEY,
  writeSessionBookingDraft,
} from "../../utils/bookingStorageKeys";
import {
  CHECK_FARE_TOAST_ID,
  flattenLaravelErrors,
  resolveApiPayloadMessage,
  resolveAxiosErrorMessage,
} from "../../utils/apiValidationErrors";

const RIDE_AIRPORT = 1;

const toApiDateTimeParts = (value) => {
  if (!value) return { date: null, time: null };
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return { date: null, time: null };
  const date = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
    dt.getDate(),
  ).padStart(2, "0")}`;
  const time = `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(
    2,
    "0",
  )}`;
  return { date, time };
};

/**
 * Terminal "Applicable On" flags from admin airport settings.
 */
function terminalFlagEnabled(value) {
  return value != false && value != 0 && value != "0" && value != "false";
}

function terminalAppliesToDirection(terminal, direction) {
  if (!direction) return true;
  if (direction == "pickup") {
    return terminalFlagEnabled(terminal?.charge_apply_pickup ?? true);
  }
  if (direction == "drop") {
    return terminalFlagEnabled(terminal?.charge_apply_drop ?? true);
  }
  return true;
}

function terminalsForDirection(airport, direction) {
  const list = Array.isArray(airport?.terminals) ? airport.terminals : [];
  // console.log("list", list);
  // console.log("direction", direction);
  if (!direction) return list;
  return list.filter((t) => terminalAppliesToDirection(t, direction));
}

/**
 * Coerce terminal_id to one that exists on the selected airport (avoids stale IDs after admin edits).
 * When direction is set, only terminals applicable for that direction are considered.
 * @returns {number|null}
 */
function resolveTerminalIdForAirport(airport, terminalId, direction = null) {
  const list = terminalsForDirection(airport, direction);
  if (!Array.isArray(list) || list.length == 0) return null;
  const tid =
    terminalId != null && terminalId !== ""
      ? String(terminalId).trim()
      : "";
  if (tid) {
    const found = list.find((t) => String(t.id) == tid);
    if (found != null && found.id != null) {
      const n = Number(found.id);
      return Number.isFinite(n) ? n : found.id;
    }
  }
  const first = list[0];
  if (first == null || first.id == null) return null;
  const n = Number(first.id);
  return Number.isFinite(n) ? n : first.id;
}

function defaultTerminalIdForAirport(airport, direction = null) {
  return resolveTerminalIdForAirport(airport, null, direction);
}

export default function AirportTransfers({
  onOpenDatePicker,
  setBookingData,
  initialData,
  parentBookingSnapshot = null,
  checkoutRevisionRef = 0,
  onCheckFareConfigChange,
}) {
  const [activeSubTab, setActiveSubTab] = useState("drop");
  const [fareLoading, setFareLoading] = useState(false);
  const [dropPickupServiceAreaError, setDropPickupServiceAreaError] =
    useState("");
  const [pickAirportServiceAreaError, setPickAirportServiceAreaError] =
    useState("");
  const [fareApiError, setFareApiError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const dropPickupInputRef = useRef(null);
  const dropAirportSelectRef = useRef(null);
  /** Run city-based airport preset only once on first airports load (not on every deps churn). */
  const citySyncPresetRanRef = useRef(false);
  const dropScheduleInputRef = useRef(null);
  const dropRoundDropoffInputRef = useRef(null);
  const dropRoundAirportSelectRef = useRef(null);
  const dropRoundScheduleInputRef = useRef(null);
  const pickAirportSelectRef = useRef(null);
  const pickDropoffInputRef = useRef(null);
  const pickScheduleInputRef = useRef(null);
  const pickRoundDropoffInputRef = useRef(null);
  const pickRoundAirportSelectRef = useRef(null);
  const pickRoundScheduleInputRef = useRef(null);

  // Drop To Airport state
  const [dropVehicle, setDropVehicle] = useState("");
  const [dropPassengers, setDropPassengers] = useState(1);
  const [dropSuitcase, setDropSuitcase] = useState(0);
  const [dropPickup, setDropPickup] = useState({
    address: "",
    lat: null,
    lng: null,
  });
  const [dropStops, setDropStops] = useState([]);
  const [dropAirport, setDropAirport] = useState(null); // Selected airport object
  const [dropTerminalId, setDropTerminalId] = useState(null);
  const [airports, setAirports] = useState([]);

  const getTerminalById = React.useCallback((airport, terminalId) => {
    if (!airport || !terminalId) return null;
    return (airport.terminals || []).find(
      (t) => String(t.id) == String(terminalId),
    );
  }, []);

  const getTerminalByName = React.useCallback((airport, terminalName) => {
    if (!airport || !terminalName) return null;
    return (airport.terminals || []).find((t) => t.name == terminalName);
  }, []);

  const handleCitySync = React.useCallback(() => {
    if (initialData) return;
    const cityId = localStorage.getItem("selectedCityId");
    if (cityId && airports.length > 0) {
      const matchedAirport = airports.find(
        (a) => String(a.work_location_id) == String(cityId),
      );
      if (matchedAirport) {
        // Pre-select for Drop To Airport
        setDropAirport(matchedAirport);
        setDropTerminalId(defaultTerminalIdForAirport(matchedAirport, "drop"));

        // Pre-select for Pick-up From Airport
        setPickAirport(matchedAirport);
        setPickTerminalId(defaultTerminalIdForAirport(matchedAirport, "pickup"));

        // Pre-select for Round Trip Return (if applicable)
        setPickRoundAirport(matchedAirport);
        setPickRoundTerminalId(
          defaultTerminalIdForAirport(matchedAirport, "drop"),
        );
        setDropRoundAirport(matchedAirport);
        setDropRoundTerminalId(
          defaultTerminalIdForAirport(matchedAirport, "pickup"),
        );
      }
    }
  }, [airports, initialData]);

  useEffect(() => {
    fetchAirports();
  }, []);

  useEffect(() => {
    window.addEventListener("cityChange", handleCitySync);
    return () => window.removeEventListener("cityChange", handleCitySync);
  }, [handleCitySync]);

  useEffect(() => {
    if (airports.length == 0) return;
    if (citySyncPresetRanRef.current) return;
    citySyncPresetRanRef.current = true;
    handleCitySync();
  }, [airports, handleCitySync]);

  const fetchAirports = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_APP_DOMAIN}/api/airports`,
      );
      if (response.data.status) {
        setAirports(response.data.airports);
      }
    } catch (error) {
      console.error("Error fetching airports:", error);
    }
  };
  const [dropSchedule, setDropSchedule] = useState("");
  const [dropFlightNo, setDropFlightNo] = useState("");
  const [showDropRoundTrip, setShowDropRoundTrip] = useState(false);
  const [dropRoundAirport, setDropRoundAirport] = useState(null);
  const [dropRoundTerminalId, setDropRoundTerminalId] = useState(null);
  const [dropRoundSchedule, setDropRoundSchedule] = useState("");
  const [dropRoundFlightNo, setDropRoundFlightNo] = useState("");
  const [dropRoundStops, setDropRoundStops] = useState([]);
  const [dropRoundDropoff, setDropRoundDropoff] = useState({
    address: "",
    lat: null,
    lng: null,
  });
  const wasDropRoundTripEnabledRef = useRef(false);
  /** Avoid re-hydrating edit prefill when `airports` loads/updates (wipes user edits). */
  const hydratedEditInitialRef = useRef(null);

  // One-time prefill when round trip is enabled.
  // Keep field editable afterwards (no forced re-sync on clear/edit).
  useEffect(() => {
    const isJustEnabled =
      showDropRoundTrip && !wasDropRoundTripEnabledRef.current;
    if (isJustEnabled && !dropRoundDropoff.address && dropPickup.address) {
      setDropRoundDropoff(dropPickup);
    }
    wasDropRoundTripEnabledRef.current = showDropRoundTrip;
  }, [showDropRoundTrip, dropPickup, dropRoundDropoff.address]);

  // Pick-up From Airport state
  const [pickVehicle, setPickVehicle] = useState("");
  const [pickPassengers, setPickPassengers] = useState(1);
  const [pickSuitcase, setPickSuitcase] = useState(0);
  const [pickAirport, setPickAirport] = useState(null);
  const [pickTerminalId, setPickTerminalId] = useState(null);
  const [pickDropoff1, setPickDropoff1] = useState({
    address: "",
    lat: null,
    lng: null,
  });
  const [pickStops, setPickStops] = useState([]);
  const [pickSchedule, setPickSchedule] = useState("");
  const [pickFlightNo, setPickFlightNo] = useState("");
  const [showPickRoundTrip, setShowPickRoundTrip] = useState(false);
  const [pickRoundDropoff, setPickRoundDropoff] = useState({
    address: "",
    lat: null,
    lng: null,
  });
  const [pickRoundAirport, setPickRoundAirport] = useState(null);
  const [pickRoundTerminalId, setPickRoundTerminalId] = useState(null);
  const [pickRoundSchedule, setPickRoundSchedule] = useState("");
  const [pickRoundFlightNo, setPickRoundFlightNo] = useState("");
  const [pickRoundStops, setPickRoundStops] = useState([]);

  const { vehicles } = useVehicles();
  const dropSelectedVehicle = useMemo(
    () => vehicles.find((v) => Number(v.id) == Number(dropVehicle)),
    [vehicles, dropVehicle],
  );
  const pickSelectedVehicle = useMemo(
    () => vehicles.find((v) => Number(v.id) == Number(pickVehicle)),
    [vehicles, pickVehicle],
  );
  const dropLimits = useMemo(
    () => getBookingQuantityLimits(dropSelectedVehicle),
    [dropSelectedVehicle],
  );
  const pickLimits = useMemo(
    () => getBookingQuantityLimits(pickSelectedVehicle),
    [pickSelectedVehicle],
  );

  useEffect(() => {
    if (!initialData || initialData.ride_type != RIDE_AIRPORT) {
      hydratedEditInitialRef.current = null;
      return;
    }
    if (airports.length == 0) return;
    if (hydratedEditInitialRef.current == initialData) return;
    hydratedEditInitialRef.current = initialData;

    {
      const isPickFromAirport = airports.some(
        (a) => a.name == initialData.pickup,
      );

      if (isPickFromAirport) {
        setActiveSubTab("pickup");
        const airport = airports.find((a) => a.name == initialData.pickup);
        if (airport) {
          setPickAirport(airport);
          const t = getTerminalByName(airport, initialData.terminal || "");
          setPickTerminalId(
            resolveTerminalIdForAirport(airport, t?.id, "pickup"),
          );
        } else {
          setPickTerminalId(null);
        }
        setPickDropoff1(
          placeFromStored({
            title: initialData.dropoff_title,
            address: initialData.dropoff || "",
            lat: initialData.dropoff_lat || null,
            lng: initialData.dropoff_lng || null,
          }),
        );
        setPickSchedule(initialData.schedule || "");
        setPickFlightNo(initialData.flight_no || "");
        setPickPassengers(initialData.no_of_passengers || 1);
        setPickSuitcase(initialData.suitcase || 1);
        if (initialData.stops && initialData.stops.length > 0)
          setPickStops(
            initialData.stops.map((s) =>
              placeFromStored({
                title: s.title,
                address: s.address,
                lat: s.lat,
                lng: s.lng,
              }),
            ),
          );
        else setPickStops([]);

        if (initialData.booking_type == 2) {
          setShowPickRoundTrip(true);
          setPickRoundDropoff(
            placeFromStored({
              title: initialData.return_pickup_title,
              address: initialData.return_pickup || "",
              lat: initialData.return_pickup_lat || null,
              lng: initialData.return_pickup_lng || null,
            }),
          );
          const ra = airports.find(
            (a) => a.name == initialData.return_dropoff,
          );
          if (ra) {
            setPickRoundAirport(ra);
            const rt = getTerminalByName(ra, initialData.return_terminal || "");
            setPickRoundTerminalId(
              resolveTerminalIdForAirport(ra, rt?.id, "drop"),
            );
          } else {
            setPickRoundTerminalId(null);
          }
          setPickRoundSchedule(initialData.return_schedule || "");
          setPickRoundFlightNo(initialData.return_flight_no || "");
          if (initialData.return_stops)
            setPickRoundStops(
              initialData.return_stops.map((s) =>
                placeFromStored({
                  title: s.title,
                  address: s.address,
                  lat: s.lat,
                  lng: s.lng,
                }),
              ),
            );
        }

        const vt =
          initialData.rides?.[0]?.vehicle_type ??
          initialData.rides?.[0]?.vehicle_type_id ??
          initialData.vehicle_type ??
          initialData.vehicle_type_id;
        if (vt) setPickVehicle(vt);
      } else {
        setActiveSubTab("drop");
        setDropPickup(
          placeFromStored({
            title: initialData.pickup_title,
            address: initialData.pickup || "",
            lat: initialData.pickup_lat || null,
            lng: initialData.pickup_lng || null,
          }),
        );
        const airport = airports.find((a) => a.name == initialData.dropoff);
        if (airport) {
          setDropAirport(airport);
          const t = getTerminalByName(airport, initialData.terminal || "");
          setDropTerminalId(
            resolveTerminalIdForAirport(airport, t?.id, "drop"),
          );
        } else {
          setDropTerminalId(null);
        }
        setDropSchedule(initialData.schedule || "");
        setDropFlightNo(initialData.flight_no || "");
        setDropPassengers(initialData.no_of_passengers || 1);
        const dsc = Number(initialData.suitcase);
        setDropSuitcase(Number.isFinite(dsc) ? dsc : 0);
        if (initialData.stops && initialData.stops.length > 0)
          setDropStops(
            initialData.stops.map((s) =>
              placeFromStored({
                title: s.title,
                address: s.address,
                lat: s.lat,
                lng: s.lng,
              }),
            ),
          );
        else setDropStops([]);

        if (initialData.booking_type == 2) {
          setShowDropRoundTrip(true);
          const ra = airports.find((a) => a.name == initialData.return_pickup);
          if (ra) {
            setDropRoundAirport(ra);
            const rt = getTerminalByName(ra, initialData.return_terminal || "");
            setDropRoundTerminalId(
              resolveTerminalIdForAirport(ra, rt?.id, "pickup"),
            );
          } else {
            setDropRoundTerminalId(null);
          }
          setDropRoundSchedule(initialData.return_schedule || "");
          setDropRoundFlightNo(initialData.return_flight_no || "");
          setDropRoundDropoff(
            placeFromStored({
              title: initialData.return_dropoff_title,
              address: initialData.return_dropoff || "",
              lat: initialData.return_dropoff_lat || null,
              lng: initialData.return_dropoff_lng || null,
            }),
          );
          if (initialData.return_stops)
            setDropRoundStops(
              initialData.return_stops.map((s) =>
                placeFromStored({
                  title: s.title,
                  address: s.address,
                  lat: s.lat,
                  lng: s.lng,
                }),
              ),
            );
          else setDropRoundStops([]);
        }

        const vt =
          initialData.rides?.[0]?.vehicle_type ??
          initialData.rides?.[0]?.vehicle_type_id ??
          initialData.vehicle_type ??
          initialData.vehicle_type_id;
        if (vt) setDropVehicle(vt);
      }
    }
  }, [initialData, airports, getTerminalByName]);

  const [activeField, setActiveField] = useState(null);
  const { suggestions, fetchSuggestions, getPlaceDetails, setSuggestions } =
    useGooglePlaces();

  useEffect(() => {
    const { passengers, suitcase } = clampQuantities({
      passengers: dropPassengers,
      suitcase: dropSuitcase,
      maxPassengers: dropLimits.maxPassengers,
      maxSuitcase: dropLimits.maxSuitcase,
    });
    if (passengers !== dropPassengers) setDropPassengers(passengers);
    if (suitcase !== dropSuitcase) setDropSuitcase(suitcase);
  }, [
    dropLimits.maxPassengers,
    dropLimits.maxSuitcase,
    dropPassengers,
    dropSuitcase,
  ]);

  useEffect(() => {
    const { passengers, suitcase } = clampQuantities({
      passengers: pickPassengers,
      suitcase: pickSuitcase,
      maxPassengers: pickLimits.maxPassengers,
      maxSuitcase: pickLimits.maxSuitcase,
    });
    if (passengers !== pickPassengers) setPickPassengers(passengers);
    if (suitcase !== pickSuitcase) setPickSuitcase(suitcase);
  }, [
    pickLimits.maxPassengers,
    pickLimits.maxSuitcase,
    pickPassengers,
    pickSuitcase,
  ]);

  const handlePlaceSelect = async (place, field) => {
    try {
      setFareApiError("");
      const details = await getPlaceDetails(place.place_id, place);
      const applyStop = async (pickup, drop, stops, setStops, idx) => {
        const routeErr = await getStopRouteValidationError(pickup, drop, details);
        if (routeErr) {
          toast.error(routeErr);
          return;
        }
        const s = [...stops];
        s[idx] = details;
        setStops(s);
      };
      if (field == "dropPickup") {
        setDropPickup(details);
        const v = await validatePickupServiceArea(details.lat, details.lng);
        setDropPickupServiceAreaError(v.ok ? "" : v.message || "");
      } else if (field == "pickDropoff1") setPickDropoff1(details);
      else if (field == "pickRoundDropoff") setPickRoundDropoff(details);
      else if (field == "dropRoundDropoff") setDropRoundDropoff(details);
      else if (field.startsWith("dropStop-")) {
        const idx = parseInt(field.split("-")[1]);
        await applyStop(dropPickup, dropAirport, dropStops, setDropStops, idx);
      } else if (field.startsWith("pickStop-")) {
        const idx = parseInt(field.split("-")[1]);
        await applyStop(pickAirport, pickDropoff1, pickStops, setPickStops, idx);
      } else if (field.startsWith("dropRoundStop-")) {
        const idx = parseInt(field.split("-")[1]);
        await applyStop(
          dropRoundAirport,
          dropRoundDropoff,
          dropRoundStops,
          setDropRoundStops,
          idx,
        );
      } else if (field.startsWith("pickRoundStop-")) {
        const idx = parseInt(field.split("-")[1]);
        await applyStop(
          pickRoundDropoff,
          pickRoundAirport,
          pickRoundStops,
          setPickRoundStops,
          idx,
        );
      }
      setSuggestions([]);
      setActiveField(null);
    } catch (error) {
      console.error(error);
    }
  };

  // Drop stops helpers
  const addDropStop = () =>
    setDropStops([...dropStops, { address: "", lat: null, lng: null }]);
  const updateDropStop = (i, v) => {
    const s = [...dropStops];
    s[i] = { ...s[i], address: v, lat: null, lng: null };
    setDropStops(s);
    setActiveField(`dropStop-${i}`);
    fetchSuggestions(v);
  };
  const removeDropStop = (i) =>
    setDropStops(dropStops.filter((_, idx) => idx !== i));

  const isDropValid =
    dropAirport &&
    hasResolvedPlaceCoords(dropAirport) &&
    dropPickup.address &&
    hasResolvedPlaceCoords(dropPickup) &&
    dropSchedule &&
    !dropPickupServiceAreaError &&
    (!showDropRoundTrip ||
      (dropRoundAirport &&
        hasResolvedPlaceCoords(dropRoundAirport) &&
        dropRoundSchedule &&
        dropRoundDropoff.address &&
        hasResolvedPlaceCoords(dropRoundDropoff)));

  // Drop round trip stops helpers
  const addDropRoundStop = () =>
    setDropRoundStops([
      ...dropRoundStops,
      { address: "", lat: null, lng: null },
    ]);
  const updateDropRoundStop = (i, v) => {
    const s = [...dropRoundStops];
    s[i] = { ...s[i], address: v, lat: null, lng: null };
    setDropRoundStops(s);
    setActiveField(`dropRoundStop-${i}`);
    fetchSuggestions(v);
  };
  const removeDropRoundStop = (i) =>
    setDropRoundStops(dropRoundStops.filter((_, idx) => idx !== i));

  // Pick stops helpers
  const addPickStop = () =>
    setPickStops([...pickStops, { address: "", lat: null, lng: null }]);
  const updatePickStop = (i, v) => {
    const s = [...pickStops];
    s[i] = { ...s[i], address: v, lat: null, lng: null };
    setPickStops(s);
    setActiveField(`pickStop-${i}`);
    fetchSuggestions(v);
  };
  const removePickStop = (i) =>
    setPickStops(pickStops.filter((_, idx) => idx !== i));

  const isPickValid =
    pickAirport &&
    hasResolvedPlaceCoords(pickAirport) &&
    pickDropoff1.address &&
    hasResolvedPlaceCoords(pickDropoff1) &&
    pickSchedule &&
    !pickAirportServiceAreaError &&
    (!showPickRoundTrip ||
      (pickRoundDropoff.address &&
        hasResolvedPlaceCoords(pickRoundDropoff) &&
        pickRoundAirport &&
        hasResolvedPlaceCoords(pickRoundAirport) &&
        pickRoundSchedule));

  const focusInvalidField = useCallback((fieldKey) => {
    const refs = {
      dropPickup: dropPickupInputRef,
      dropAirport: dropAirportSelectRef,
      dropSchedule: dropScheduleInputRef,
      dropRoundDropoff: dropRoundDropoffInputRef,
      dropRoundAirport: dropRoundAirportSelectRef,
      dropRoundSchedule: dropRoundScheduleInputRef,
      pickAirport: pickAirportSelectRef,
      pickDropoff: pickDropoffInputRef,
      pickSchedule: pickScheduleInputRef,
      pickRoundDropoff: pickRoundDropoffInputRef,
      pickRoundAirport: pickRoundAirportSelectRef,
      pickRoundSchedule: pickRoundScheduleInputRef,
    };
    refs[fieldKey]?.current?.focus?.();
  }, []);

  const getCheckFareValidation = useCallback((frm) => {
    const isDrop = frm.activeSubTab == "drop";
    if (isDrop) {
      if (!frm.dropPickup.address) {
        return {
          errors: { dropPickup: "Pick-up is required." },
          firstField: "dropPickup",
        };
      }
      if (!hasResolvedPlaceCoords(frm.dropPickup)) {
        return {
          errors: {
            dropPickup: "Select pick-up from suggestions to confirm location.",
          },
          firstField: "dropPickup",
        };
      }
      if (!frm.dropAirport || !hasResolvedPlaceCoords(frm.dropAirport)) {
        return {
          errors: { dropAirport: "Airport is required." },
          firstField: "dropAirport",
        };
      }
      if (!frm.dropSchedule) {
        return {
          errors: { dropSchedule: "Pick-up time is required." },
          firstField: "dropSchedule",
        };
      }
      const dropLeadErr = getPickupMinLeadError(frm.dropSchedule);
      if (dropLeadErr) {
        return {
          errors: { dropSchedule: dropLeadErr },
          firstField: "dropSchedule",
        };
      }
      if (frm.dropPickupServiceAreaError) {
        return { errors: {}, firstField: "dropPickup" };
      }
      if (frm.showDropRoundTrip) {
        if (!frm.dropRoundDropoff.address) {
          return {
            errors: { dropRoundDropoff: "Return drop-off is required." },
            firstField: "dropRoundDropoff",
          };
        }
        if (!hasResolvedPlaceCoords(frm.dropRoundDropoff)) {
          return {
            errors: {
              dropRoundDropoff:
                "Select return drop-off from suggestions to confirm location.",
            },
            firstField: "dropRoundDropoff",
          };
        }
        if (
          !frm.dropRoundAirport ||
          !hasResolvedPlaceCoords(frm.dropRoundAirport)
        ) {
          return {
            errors: { dropRoundAirport: "Return airport is required." },
            firstField: "dropRoundAirport",
          };
        }
        if (!frm.dropRoundSchedule) {
          return {
            errors: { dropRoundSchedule: "Return pick-up time is required." },
            firstField: "dropRoundSchedule",
          };
        }
      }
      return { errors: {}, firstField: null };
    }

    if (!frm.pickAirport || !hasResolvedPlaceCoords(frm.pickAirport)) {
      return {
        errors: { pickAirport: "Airport is required." },
        firstField: "pickAirport",
      };
    }
    if (frm.pickAirportServiceAreaError) {
      return { errors: {}, firstField: "pickAirport" };
    }
    if (!frm.pickDropoff1.address) {
      return {
        errors: { pickDropoff: "Drop-off is required." },
        firstField: "pickDropoff",
      };
    }
    if (!hasResolvedPlaceCoords(frm.pickDropoff1)) {
      return {
        errors: {
          pickDropoff: "Select drop-off from suggestions to confirm location.",
        },
        firstField: "pickDropoff",
      };
    }
    if (!frm.pickSchedule) {
      return {
        errors: { pickSchedule: "Pick-up time is required." },
        firstField: "pickSchedule",
      };
    }
    const pickLeadErr = getPickupMinLeadError(frm.pickSchedule);
    if (pickLeadErr) {
      return {
        errors: { pickSchedule: pickLeadErr },
        firstField: "pickSchedule",
      };
    }
    if (frm.showPickRoundTrip) {
      if (!frm.pickRoundDropoff.address) {
        return {
          errors: { pickRoundDropoff: "Return drop-off is required." },
          firstField: "pickRoundDropoff",
        };
      }
      if (!hasResolvedPlaceCoords(frm.pickRoundDropoff)) {
        return {
          errors: {
            pickRoundDropoff:
              "Select return drop-off from suggestions to confirm location.",
          },
          firstField: "pickRoundDropoff",
        };
      }
      if (
        !frm.pickRoundAirport ||
        !hasResolvedPlaceCoords(frm.pickRoundAirport)
      ) {
        return {
          errors: { pickRoundAirport: "Return airport is required." },
          firstField: "pickRoundAirport",
        };
      }
      if (!frm.pickRoundSchedule) {
        return {
          errors: { pickRoundSchedule: "Return pick-up time is required." },
          firstField: "pickRoundSchedule",
        };
      }
    }
    return { errors: {}, firstField: null };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!pickAirport?.lat || !pickAirport?.lng) {
        setPickAirportServiceAreaError("");
        return;
      }
      const v = await validatePickupServiceArea(
        pickAirport.lat,
        pickAirport.lng,
      );
      if (!cancelled) {
        setPickAirportServiceAreaError(v.ok ? "" : v.message || "");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pickAirport]);

  // Pick round trip stops helpers
  const addPickRoundStop = () =>
    setPickRoundStops([
      ...pickRoundStops,
      { address: "", lat: null, lng: null },
    ]);
  const updatePickRoundStop = (i, v) => {
    const s = [...pickRoundStops];
    s[i] = { ...s[i], address: v, lat: null, lng: null };
    setPickRoundStops(s);
    setActiveField(`pickRoundStop-${i}`);
    fetchSuggestions(v);
  };
  const removePickRoundStop = (i) =>
    setPickRoundStops(pickRoundStops.filter((_, idx) => idx !== i));

  /** Shared with handleCheckFare + checkout edit preview (Back without re–Check Fare). */
  const buildAirportBookingDisplayForCheckout = useCallback(
    (fareDetails) => {
      const isDrop = activeSubTab == "drop";
      const isRoundTrip = isDrop ? showDropRoundTrip : showPickRoundTrip;
      const pickupSchedule = isDrop ? dropSchedule : pickSchedule;
      const returnSchedule = isDrop ? dropRoundSchedule : pickRoundSchedule;
      const pickupParts = toApiDateTimeParts(pickupSchedule);
      const returnParts = toApiDateTimeParts(returnSchedule);

      const mainAirportForTerm = isDrop ? dropAirport : pickAirport;
      const onwardDirection = isDrop ? "drop" : "pickup";
      const onwardResolvedTid = resolveTerminalIdForAirport(
        mainAirportForTerm,
        isDrop ? dropTerminalId : pickTerminalId,
        onwardDirection,
      );

      const ridePayload = {
        vehicle_type: isDrop ? dropVehicle : pickVehicle,
        pickup_lat: isDrop ? dropPickup.lat : pickAirport?.lat,
        pickup_lng: isDrop ? dropPickup.lng : pickAirport?.lng,
        drop_lat: isDrop ? dropAirport?.lat : pickDropoff1.lat,
        drop_lng: isDrop ? dropAirport?.lng : pickDropoff1.lng,
        airport_id: (isDrop ? dropAirport : pickAirport)?.id || null,
        airport_direction: isDrop ? "drop" : "pickup",
        pickup_date: pickupParts.date,
        pickup_time: pickupParts.time,
        terminal_id: onwardResolvedTid,
        no_of_passengers: isDrop ? dropPassengers : pickPassengers,
        suitcase: isDrop ? dropSuitcase : pickSuitcase,
        stops: (isDrop ? dropStops : pickStops)
          .filter((s) => s.address && s.address.trim() !== "")
          .map((s) => ({ lat: s.lat, lng: s.lng })),
      };

      if (isRoundTrip) {
        ridePayload.return_date = returnParts.date;
        ridePayload.return_time = returnParts.time;
        if (isDrop) {
          ridePayload.return_pickup_lat = dropRoundAirport?.lat;
          ridePayload.return_pickup_lng = dropRoundAirport?.lng;
          ridePayload.return_drop_lat = dropRoundDropoff.lat;
          ridePayload.return_drop_lng = dropRoundDropoff.lng;
          ridePayload.return_stops = dropRoundStops
            .filter((s) => s.address && s.address.trim() !== "")
            .map((s) => ({ lat: s.lat, lng: s.lng }));
          ridePayload.return_flight_no = dropRoundFlightNo || null;
          ridePayload.return_terminal_id = resolveTerminalIdForAirport(
            dropRoundAirport,
            dropRoundTerminalId,
            "pickup",
          );
        } else {
          ridePayload.return_pickup_lat = pickRoundDropoff.lat;
          ridePayload.return_pickup_lng = pickRoundDropoff.lng;
          ridePayload.return_drop_lat = pickRoundAirport?.lat;
          ridePayload.return_drop_lng = pickRoundAirport?.lng;
          ridePayload.return_stops = pickRoundStops
            .filter((s) => s.address && s.address.trim() !== "")
            .map((s) => ({ lat: s.lat, lng: s.lng }));
          ridePayload.return_flight_no = pickRoundFlightNo || null;
          ridePayload.return_terminal_id = resolveTerminalIdForAirport(
            pickRoundAirport,
            pickRoundTerminalId,
            "drop",
          );
        }
      }

      const payload = {
        ride_type: RIDE_AIRPORT,
        booking_type: isRoundTrip ? 2 : 1,
        no_of_passengers: isDrop ? dropPassengers : pickPassengers,
        suitcase: isDrop ? dropSuitcase : pickSuitcase,
        rides: [ridePayload],
      };

      const selectedAirport = mainAirportForTerm;
      const selectedTerminal = getTerminalById(
        selectedAirport,
        onwardResolvedTid,
      );

      return {
        ...payload,
        pickup: isDrop
          ? getPlaceFullAddress(dropPickup)
          : pickAirport?.name,
        pickup_title: isDrop ? dropPickup.address : pickAirport?.name,
        pickup_lat: payload.rides[0].pickup_lat,
        pickup_lng: payload.rides[0].pickup_lng,
        dropoff: isDrop
          ? dropAirport?.name
          : getPlaceFullAddress(pickDropoff1),
        dropoff_title: isDrop ? dropAirport?.name : pickDropoff1.address,
        dropoff_lat: payload.rides[0].drop_lat,
        dropoff_lng: payload.rides[0].drop_lng,
        airport_id: payload.rides[0].airport_id ?? null,
        terminal_id: onwardResolvedTid,
        airport_direction: isDrop ? "drop" : "pickup",
        terminal: selectedTerminal?.name || null,
        stops: (isDrop ? dropStops : pickStops)
          .filter((s) => getPlaceFullAddress(s))
          .map((s) => placeToStored(s)),
        schedule: isDrop ? dropSchedule : pickSchedule,
        flight_no: isDrop ? dropFlightNo : pickFlightNo,
        no_of_passengers: isDrop ? dropPassengers : pickPassengers,
        suitcase: isDrop ? dropSuitcase : pickSuitcase,
        fare_details: fareDetails,
        distance:
          fareDetails?.distance_km != null && fareDetails?.distance_km !== ""
            ? Number(fareDetails.distance_km)
            : null,
        return_distance:
          fareDetails?.return_distance_km != null &&
          fareDetails?.return_distance_km !== ""
            ? Number(fareDetails.return_distance_km)
            : null,
        return_pickup: isDrop
          ? showDropRoundTrip
            ? dropRoundAirport?.name
            : null
          : showPickRoundTrip
            ? getPlaceFullAddress(pickRoundDropoff)
            : null,
        return_pickup_title: isDrop
          ? showDropRoundTrip
            ? dropRoundAirport?.name
            : null
          : showPickRoundTrip
            ? pickRoundDropoff.address
            : null,
        return_pickup_lat: isDrop
          ? showDropRoundTrip
            ? dropRoundAirport?.lat
            : null
          : showPickRoundTrip
            ? pickRoundDropoff.lat
            : null,
        return_pickup_lng: isDrop
          ? showDropRoundTrip
            ? dropRoundAirport?.lng
            : null
          : showPickRoundTrip
            ? pickRoundDropoff.lng
            : null,
        return_dropoff: isDrop
          ? showDropRoundTrip
            ? getPlaceFullAddress(dropRoundDropoff)
            : null
          : showPickRoundTrip
            ? pickRoundAirport?.name
            : null,
        return_dropoff_title: isDrop
          ? showDropRoundTrip
            ? dropRoundDropoff.address
            : null
          : showPickRoundTrip
            ? pickRoundAirport?.name
            : null,
        return_dropoff_lat: isDrop
          ? showDropRoundTrip
            ? dropRoundDropoff.lat
            : null
          : showPickRoundTrip
            ? pickRoundAirport?.lat
            : null,
        return_dropoff_lng: isDrop
          ? showDropRoundTrip
            ? dropRoundDropoff.lng
            : null
          : showPickRoundTrip
            ? pickRoundAirport?.lng
            : null,
        return_terminal: isDrop
          ? showDropRoundTrip
            ? getTerminalById(
                dropRoundAirport,
                resolveTerminalIdForAirport(
                  dropRoundAirport,
                  dropRoundTerminalId,
                  "pickup",
                ),
              )?.name || null
            : null
          : showPickRoundTrip
            ? getTerminalById(
                pickRoundAirport,
                resolveTerminalIdForAirport(
                  pickRoundAirport,
                  pickRoundTerminalId,
                  "drop",
                ),
              )?.name || null
            : null,
        return_terminal_id: isDrop
          ? showDropRoundTrip
            ? resolveTerminalIdForAirport(
                dropRoundAirport,
                dropRoundTerminalId,
                "pickup",
              )
            : null
          : showPickRoundTrip
            ? resolveTerminalIdForAirport(
                pickRoundAirport,
                pickRoundTerminalId,
                "drop",
              )
            : null,
        return_schedule: isDrop
          ? showDropRoundTrip
            ? dropRoundSchedule
            : null
          : showPickRoundTrip
            ? pickRoundSchedule
            : null,
        return_flight_no: isDrop
          ? showDropRoundTrip
            ? dropRoundFlightNo
            : null
          : showPickRoundTrip
            ? pickRoundFlightNo
            : null,
        return_stops: (isDrop ? dropRoundStops : pickRoundStops)
          .filter((s) => getPlaceFullAddress(s))
          .map((s) => placeToStored(s)),
      };
    },
    [
      activeSubTab,
      showDropRoundTrip,
      showPickRoundTrip,
      dropSchedule,
      pickSchedule,
      dropRoundSchedule,
      pickRoundSchedule,
      dropVehicle,
      pickVehicle,
      dropPickup,
      pickAirport,
      dropAirport,
      pickDropoff1,
      dropTerminalId,
      pickTerminalId,
      dropPassengers,
      pickPassengers,
      dropSuitcase,
      pickSuitcase,
      dropStops,
      pickStops,
      dropRoundAirport,
      dropRoundDropoff,
      dropRoundStops,
      pickRoundDropoff,
      pickRoundAirport,
      pickRoundStops,
      dropRoundFlightNo,
      pickRoundFlightNo,
      dropRoundTerminalId,
      pickRoundTerminalId,
      dropFlightNo,
      pickFlightNo,
      getTerminalById,
    ],
  );

  useLayoutEffect(() => {
    if (!initialData || initialData.ride_type !== RIDE_AIRPORT) return;
    const isDrop = activeSubTab == "drop";
    if (!(isDrop ? isDropValid : isPickValid)) return;
    const preview = buildAirportBookingDisplayForCheckout(
      parentBookingSnapshot?.fare_details ?? initialData?.fare_details,
    );
    if (!preview) return;
    try {
      const ts = Math.max(Date.now(), (checkoutRevisionRef || 0) + 1);
      sessionStorage.setItem(
        BOOKING_DRAFT_STORAGE_KEY,
        JSON.stringify({ ts, data: preview }),
      );
    } catch (_) {
      /* ignore */
    }
  }, [
    checkoutRevisionRef,
    initialData,
    parentBookingSnapshot,
    activeSubTab,
    isDropValid,
    isPickValid,
    buildAirportBookingDisplayForCheckout,
  ]);

  /** Latest form snapshot on each click (parent keeps stable onClick — avoids stale lat/lng). */
  const checkFareStateRef = useRef(null);
  checkFareStateRef.current = {
    activeSubTab,
    isDropValid,
    isPickValid,
    dropSchedule,
    pickSchedule,
    dropRoundSchedule,
    pickRoundSchedule,
    showDropRoundTrip,
    showPickRoundTrip,
    dropVehicle,
    pickVehicle,
    dropPickup,
    pickAirport,
    pickDropoff1,
    dropAirport,
    dropStops,
    pickStops,
    dropTerminalId,
    pickTerminalId,
    dropPassengers,
    pickPassengers,
    dropSuitcase,
    pickSuitcase,
    dropRoundAirport,
    dropRoundDropoff,
    dropRoundStops,
    dropRoundFlightNo,
    dropRoundTerminalId,
    pickRoundDropoff,
    pickRoundAirport,
    pickRoundStops,
    pickRoundFlightNo,
    pickRoundTerminalId,
    dropPickupServiceAreaError,
    pickAirportServiceAreaError,
    buildAirportBookingDisplayForCheckout,
  };

  const handleCheckFare = useCallback(async () => {
    const frm = checkFareStateRef.current;

    const isDropTab = frm.activeSubTab == "drop";
    if (isDropTab) {
      let stopErr = await validateStopsOnRoute(
        frm.dropPickup,
        frm.dropAirport,
        frm.dropStops,
      );
      if (stopErr) {
        toast.error(stopErr.message);
        focusInvalidField("dropPickup");
        return;
      }
      if (frm.showDropRoundTrip) {
        stopErr = await validateStopsOnRoute(
          frm.dropRoundAirport,
          frm.dropRoundDropoff,
          frm.dropRoundStops,
        );
        if (stopErr) {
          toast.error(stopErr.message);
          focusInvalidField("dropRoundAirport");
          return;
        }
      }
    } else {
      let stopErr = await validateStopsOnRoute(
        frm.pickAirport,
        frm.pickDropoff1,
        frm.pickStops,
      );
      if (stopErr) {
        toast.error(stopErr.message);
        focusInvalidField("pickAirport");
        return;
      }
      if (frm.showPickRoundTrip) {
        stopErr = await validateStopsOnRoute(
          frm.pickRoundDropoff,
          frm.pickRoundAirport,
          frm.pickRoundStops,
        );
        if (stopErr) {
          toast.error(stopErr.message);
          focusInvalidField("pickRoundAirport");
          return;
        }
      }
    }

    const { errors, firstField } = getCheckFareValidation(frm);
    if (firstField) {
      setFormErrors(errors);
      focusInvalidField(firstField);
      return;
    }

    setFormErrors({});

    const isDrop = frm.activeSubTab == "drop";
    const pickupSchedule = isDrop ? frm.dropSchedule : frm.pickSchedule;
    const returnSchedule = isDrop
      ? frm.dropRoundSchedule
      : frm.pickRoundSchedule;

    if (isDrop ? frm.showDropRoundTrip : frm.showPickRoundTrip) {
      const pickupDate = new Date(pickupSchedule);
      const returnDate = new Date(returnSchedule);
      if (
        !returnSchedule ||
        Number.isNaN(returnDate.getTime()) ||
        returnDate <= pickupDate
      ) {
        toast.error("Return time must be after pickup time.");
        return;
      }
    }

    setFareLoading(true);
    setFareApiError("");
    toast.dismiss(CHECK_FARE_TOAST_ID);
    try {
        const isRoundTrip = isDrop
          ? frm.showDropRoundTrip
          : frm.showPickRoundTrip;
        const pickupParts = toApiDateTimeParts(pickupSchedule);
        const returnParts = toApiDateTimeParts(returnSchedule);

        const mainAp = isDrop ? frm.dropAirport : frm.pickAirport;
        const onwardDirection = isDrop ? "drop" : "pickup";
        const onwardApplicable = terminalsForDirection(
          mainAp,
          onwardDirection,
        );
        const onwardTid = resolveTerminalIdForAirport(
          mainAp,
          isDrop ? frm.dropTerminalId : frm.pickTerminalId,
          onwardDirection,
        );
        if (onwardApplicable.length > 0 && onwardTid == null) {
          setFareLoading(false);
          toast.error(
            "Could not resolve a valid terminal for this airport. Please re-select the airport.",
            { toastId: CHECK_FARE_TOAST_ID },
          );
          return;
        }

        const ridePayload = {
          vehicle_type: isDrop ? frm.dropVehicle : frm.pickVehicle,
          pickup_lat: isDrop ? frm.dropPickup.lat : frm.pickAirport?.lat,
          pickup_lng: isDrop ? frm.dropPickup.lng : frm.pickAirport?.lng,
          drop_lat: isDrop ? frm.dropAirport?.lat : frm.pickDropoff1.lat,
          drop_lng: isDrop ? frm.dropAirport?.lng : frm.pickDropoff1.lng,
          airport_id: (isDrop ? frm.dropAirport : frm.pickAirport)?.id || null,
          airport_direction: isDrop ? "drop" : "pickup",
          pickup_date: pickupParts.date,
          pickup_time: pickupParts.time,
          terminal_id: onwardTid,
          no_of_passengers: isDrop ? frm.dropPassengers : frm.pickPassengers,
          suitcase: isDrop ? frm.dropSuitcase : frm.pickSuitcase,
          stops: (isDrop ? frm.dropStops : frm.pickStops)
            .filter((row) => row.address && row.address.trim() !== "")
            .map((row) => ({ lat: row.lat, lng: row.lng })),
        };

        if (isRoundTrip) {
          ridePayload.return_date = returnParts.date;
          ridePayload.return_time = returnParts.time;
          if (isDrop) {
            ridePayload.return_pickup_lat = frm.dropRoundAirport?.lat;
            ridePayload.return_pickup_lng = frm.dropRoundAirport?.lng;
            ridePayload.return_drop_lat = frm.dropRoundDropoff.lat;
            ridePayload.return_drop_lng = frm.dropRoundDropoff.lng;
            ridePayload.return_stops = frm.dropRoundStops
              .filter((row) => row.address && row.address.trim() !== "")
              .map((row) => ({ lat: row.lat, lng: row.lng }));
            ridePayload.return_flight_no = frm.dropRoundFlightNo || null;
            const retTid = resolveTerminalIdForAirport(
              frm.dropRoundAirport,
              frm.dropRoundTerminalId,
              "pickup",
            );
            const retApplicable = terminalsForDirection(
              frm.dropRoundAirport,
              "pickup",
            );
            if (retApplicable.length > 0 && retTid == null) {
              setFareLoading(false);
              toast.error(
                "Could not resolve a valid return terminal. Please re-select the airport.",
                { toastId: CHECK_FARE_TOAST_ID },
              );
              return;
            }
            ridePayload.return_terminal_id = retTid;
          } else {
            ridePayload.return_pickup_lat = frm.pickRoundDropoff.lat;
            ridePayload.return_pickup_lng = frm.pickRoundDropoff.lng;
            ridePayload.return_drop_lat = frm.pickRoundAirport?.lat;
            ridePayload.return_drop_lng = frm.pickRoundAirport?.lng;
            ridePayload.return_stops = frm.pickRoundStops
              .filter((row) => row.address && row.address.trim() !== "")
              .map((row) => ({ lat: row.lat, lng: row.lng }));
            ridePayload.return_flight_no = frm.pickRoundFlightNo || null;
            const retTid = resolveTerminalIdForAirport(
              frm.pickRoundAirport,
              frm.pickRoundTerminalId,
              "drop",
            );
            const retApplicable = terminalsForDirection(
              frm.pickRoundAirport,
              "drop",
            );
            if (retApplicable.length > 0 && retTid == null) {
              setFareLoading(false);
              toast.error(
                "Could not resolve a valid return terminal. Please re-select the airport.",
                { toastId: CHECK_FARE_TOAST_ID },
              );
              return;
            }
            ridePayload.return_terminal_id = retTid;
          }
        }

        const payload = {
          ride_type: RIDE_AIRPORT,
          booking_type: isRoundTrip ? 2 : 1,
          no_of_passengers: isDrop ? frm.dropPassengers : frm.pickPassengers,
          suitcase: isDrop ? frm.dropSuitcase : frm.pickSuitcase,
          rides: [ridePayload],
        };

        const response = await axios.post(
          `${process.env.REACT_APP_APP_DOMAIN}/api/customer/check-fare`,
          payload,
        );

        if (response.data.status) {
          setFareApiError("");
          toast.dismiss(CHECK_FARE_TOAST_ID);
          setDropPickupServiceAreaError("");
          setPickAirportServiceAreaError("");

          const next = frm.buildAirportBookingDisplayForCheckout(
            response.data.fare_details,
          );
          if (next) {
            setBookingData(next);
            writeSessionBookingDraft(next);
          }

          setTimeout(() => {
            const modalElement = document.getElementById("updatePolicy-Modal");
            if (modalElement && window.bootstrap) {
              const modal =
                window.bootstrap.Modal.getOrCreateInstance(modalElement);
              modal.show();
            }
          }, 200);
          console.log("Modal show triggered");
        } else {
          const data = response.data;
          const inline = flattenLaravelErrors(data?.errors).join("\n");
          const msg =
            resolveApiPayloadMessage(data) ||
            inline.replace(/\n/g, " ").trim() ||
            "Something went wrong";
          setFareApiError(inline || msg);
          if (isOutsideServiceAreaMessage(msg)) {
            if (isDrop) setDropPickupServiceAreaError(msg);
            else setPickAirportServiceAreaError(msg);
          }
          toast.error(msg, { toastId: CHECK_FARE_TOAST_ID });
        }
      } catch (error) {
        console.error("Fare check error:", error);
        const data = error.response?.data;
        const inline = flattenLaravelErrors(data?.errors).join("\n");
        const msg =
          resolveAxiosErrorMessage(error) ||
          inline.replace(/\n/g, " ").trim() ||
          "Failed to check fare. Please try again.";
        setFareApiError(inline || msg);
        if (isOutsideServiceAreaMessage(msg)) {
          if (isDrop) setDropPickupServiceAreaError(msg);
          else setPickAirportServiceAreaError(msg);
        }
        toast.error(msg, { toastId: CHECK_FARE_TOAST_ID });
      } finally {
        setFareLoading(false);
      }
  }, [focusInvalidField, getCheckFareValidation, setBookingData]);

  // Expose check-fare button state to parent (BookingPage)
  useEffect(() => {
    if (!onCheckFareConfigChange) return;
    onCheckFareConfigChange({
      onClick: handleCheckFare,
      disabled: fareLoading,
      label: fareLoading ? "Calculating..." : "Check Fare",
    });
  }, [
    onCheckFareConfigChange,
    fareLoading,
    handleCheckFare,
  ]);

  return (
    <div
      className="tab-pane fade active show"
      id="nav-home"
      role="tabpanel"
      aria-labelledby="nav-home-tab"
    >
      <div className="second_tabs">
        <h2>Airport Transfers</h2>
        {fareApiError ? (
          <div
            className="alert alert-danger my-2"
            role="alert"
            style={{ whiteSpace: "pre-line" }}
          >
            {fareApiError}
          </div>
        ) : null}
        <nav className="nav_second">
          <div className="nav nav-tabs" id="nav-tab2" role="tablist">
            <button
              className={`nav-link${activeSubTab == "drop" ? " active" : ""}`}
              type="button"
              role="tab"
              onClick={() => setActiveSubTab("drop")}
            >
              Drop To Airport
            </button>
            <button
              className={`nav-link${activeSubTab == "pickup" ? " active" : ""}`}
              type="button"
              role="tab"
              onClick={() => setActiveSubTab("pickup")}
            >
              Pick-up From Airport
            </button>
          </div>
        </nav>
      </div>

      {/* ---- Drop To Airport ---- */}
      <div style={{ display: activeSubTab == "drop" ? "block" : "none" }}>
        <div className="transport_colMain">
          <VehicleSelector
            selected={dropVehicle}
            onSelect={setDropVehicle}
            disableAutoSelect={Boolean(initialData)}
          />
          <div className="form_col">
            <div className="form_title">
              <h3>Location </h3>
              <a
                href="#!"
                className="info_modal"
                onClick={(e) => {
                  e.preventDefault();
                  addDropStop();
                }}
              >
                + Add Stop
              </a>
            </div>
            <div className="input_col">
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/location_on.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className={`form-control${
                      formErrors.dropPickup || dropPickupServiceAreaError
                        ? " is-invalid"
                        : ""
                    }`}
                    placeholder="Pick-up"
                    value={dropPickup.address}
                    ref={dropPickupInputRef}
                    onChange={(e) => {
                      if (formErrors.dropPickup) {
                        setFormErrors((prev) => ({ ...prev, dropPickup: "" }));
                      }
                      setDropPickup({
                        address: e.target.value,
                        lat: null,
                        lng: null,
                      });
                      setFareApiError("");
                      setDropPickupServiceAreaError("");
                      setActiveField("dropPickup");
                      fetchSuggestions(e.target.value);
                    }}
                  />
                  {dropPickup.address && (
                    <img
                      src="/assets/img/all-images/close-icon.png"
                      alt=""
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setDropPickup({ address: "", lat: null, lng: null });
                        setDropPickupServiceAreaError("");
                      }}
                    />
                  )}
                  {activeField == "dropPickup" && suggestions.length > 0 && (
                    <ul className="autocomplete-suggestions">
                      {suggestions.map((s) => (
                        <li
                          key={s.place_id}
                          className="autocomplete-suggestion-item"
                          onClick={() => handlePlaceSelect(s, "dropPickup")}
                        >
                          <PlaceSuggestionLabel suggestion={s} />
                        </li>
                      ))}
                    </ul>
                  )}
                  {formErrors.dropPickup || dropPickupServiceAreaError ? (
                    <div className="text-danger small mt-1 invalid-feedback d-block">
                      {formErrors.dropPickup || dropPickupServiceAreaError}
                    </div>
                  ) : null}
                </div>
              </div>
              {dropStops.map((stop, index) => (
                <div className="form-group" key={index}>
                  <div className="input_image">
                    <img src="/assets/img/all-images/location_on.png" alt="" />
                  </div>
                  <div className="input_box">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Add Stop"
                      value={stop.address}
                      onChange={(e) => updateDropStop(index, e.target.value)}
                    />
                    <img
                      src="/assets/img/all-images/close-icon.png"
                      alt=""
                      style={{ cursor: "pointer" }}
                      onClick={() => removeDropStop(index)}
                    />
                    {activeField == `dropStop-${index}` &&
                      suggestions.length > 0 && (
                        <ul className="autocomplete-suggestions">
                          {suggestions.map((s) => (
                            <li
                              key={s.place_id}
                              className="autocomplete-suggestion-item"
                              onClick={() =>
                                handlePlaceSelect(s, `dropStop-${index}`)
                              }
                            >
                              <PlaceSuggestionLabel suggestion={s} />
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                </div>
              ))}
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/plane-icon.png" alt="" />
                </div>
                <div className="input_box">
                  <select
                    className={`form-control${formErrors.dropAirport ? " is-invalid" : ""}`}
                    value={dropAirport?.id || ""}
                    ref={dropAirportSelectRef}
                    onChange={(e) => {
                      if (formErrors.dropAirport) {
                        setFormErrors((prev) => ({ ...prev, dropAirport: "" }));
                      }
                      const airport = airports.find(
                        (a) => String(a.id) == String(e.target.value),
                      );
                      setDropAirport(airport);
                      setDropTerminalId(
                        defaultTerminalIdForAirport(airport, "drop"),
                      );
                    }}
                  >
                    <option value="" disabled>
                      Select Airport
                    </option>
                    {airports.map((airport) => (
                      <option key={airport.id} value={airport.id}>
                        {airport.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.dropAirport ? (
                    <div className="invalid-feedback d-block">
                      {formErrors.dropAirport}
                    </div>
                  ) : null}
                  {terminalsForDirection(dropAirport, "drop").length > 0 ? (
                    <div className="t2">
                      {terminalsForDirection(dropAirport, "drop").map(
                        (terminal) => (
                          <span
                            key={terminal.id}
                            className={
                              String(dropTerminalId) == String(terminal.id)
                                ? "active"
                                : ""
                            }
                            onClick={() => setDropTerminalId(terminal.id)}
                            style={{ cursor: "pointer" }}
                          >
                            {terminal.name}
                          </span>
                        ),
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="form_col">
            <div className="form_title">
              <h3>Schedule </h3>
            </div>
            <div className="input_col input_col2">
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/Calendar-icon.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className={`form-control${formErrors.dropSchedule ? " is-invalid" : ""}`}
                    placeholder="Pick-up"
                    value={dropSchedule}
                    ref={dropScheduleInputRef}
                    onChange={(e) => {
                      if (formErrors.dropSchedule) {
                        setFormErrors((prev) => ({ ...prev, dropSchedule: "" }));
                      }
                      setDropSchedule(e.target.value);
                    }}
                    onClick={() =>
                      onOpenDatePicker(setDropSchedule, {}, dropSchedule)
                    }
                    data-bs-toggle="modal"
                    data-bs-target="#dateSelectModal"
                    readOnly
                    style={{ cursor: "pointer" }}
                  />
                  {formErrors.dropSchedule ? (
                    <div className="invalid-feedback d-block">
                      {formErrors.dropSchedule}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="form_col">
            <div className="form_title">
              <h3>Flight No. </h3>
              <a
                href="#!"
                className="info_modal"
                data-bs-toggle="modal"
                data-bs-target="#infoModal"
                onClick={(e) => e.preventDefault()}
              >
                More Info
              </a>
            </div>
            <div className="input_col input_col2">
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/plane-icon.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Flight No. (optional)"
                    value={dropFlightNo}
                    onChange={(e) => setDropFlightNo(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="form_col">
            <div className="form_title">
              <h3>Guest Info</h3>
            </div>
            <div className="input_col input_col2">
              <div className="form-group form-groupQuantity">
                <QuantityControl
                  label="Passengers"
                  value={dropPassengers}
                  onChange={setDropPassengers}
                  min={1}
                  max={dropLimits.maxPassengers}
                />
                <QuantityControl
                  label="Suitcase"
                  value={dropSuitcase}
                  onChange={setDropSuitcase}
                  min={0}
                  max={dropLimits.maxSuitcase}
                />
              </div>
            </div>
          </div>

          {/* Round Trip Toggle */}
          {/* HIDDEN TEMPORARILY - Round Trip button for Drop to Airport
          {!showDropRoundTrip && (
            <div className="form_col">
              <div className="input_col input_col2">
                <div className="round_trip text-end">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowDropRoundTrip(true)}
                  >
                    Round Trip
                  </button>
                </div>
              </div>
            </div>
          )}
          */}

          {showDropRoundTrip && (
            <>
              <div className="form_col">
                <div className="input_col input_col2">
                  <div className="form_title round_trip text-end">
                    <h3>Round Trip</h3>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setShowDropRoundTrip(false)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
              <div className="form_col">
                <div className="form_title">
                  <h3>Location </h3>
                  <a
                    href="#!"
                    className="info_modal"
                    onClick={(e) => {
                      e.preventDefault();
                      addDropRoundStop();
                    }}
                  >
                    + Add Stop
                  </a>
                </div>
                <div className="input_col">
                  <div className="form-group">
                    <div className="input_image">
                      <img src="/assets/img/all-images/plane-icon.png" alt="" />
                    </div>
                    <div className="input_box">
                      <select
                        className={`form-control${formErrors.dropRoundAirport ? " is-invalid" : ""}`}
                        value={dropRoundAirport?.id || ""}
                        ref={dropRoundAirportSelectRef}
                        onChange={(e) => {
                          if (formErrors.dropRoundAirport) {
                            setFormErrors((prev) => ({
                              ...prev,
                              dropRoundAirport: "",
                            }));
                          }
                          const airport = airports.find(
                            (a) => String(a.id) == String(e.target.value),
                          );
                          setDropRoundAirport(airport);
                          setDropRoundTerminalId(
                            defaultTerminalIdForAirport(airport, "pickup"),
                          );
                        }}
                      >
                        <option value="" disabled>
                          Select Airport
                        </option>
                        {airports.map((airport) => (
                          <option key={airport.id} value={airport.id}>
                            {airport.name}
                          </option>
                        ))}
                      </select>
                      {formErrors.dropRoundAirport ? (
                        <div className="invalid-feedback d-block">
                          {formErrors.dropRoundAirport}
                        </div>
                      ) : null}
                      {terminalsForDirection(dropRoundAirport, "pickup")
                        .length > 0 ? (
                        <div className="t2">
                          {terminalsForDirection(
                            dropRoundAirport,
                            "pickup",
                          ).map((terminal) => (
                            <span
                              key={terminal.id}
                              className={
                                String(dropRoundTerminalId) ==
                                String(terminal.id)
                                  ? "active"
                                  : ""
                              }
                              onClick={() =>
                                setDropRoundTerminalId(terminal.id)
                              }
                              style={{ cursor: "pointer" }}
                            >
                              {terminal.name}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {dropRoundStops.map((stop, index) => (
                    <div className="form-group" key={index}>
                      <div className="input_image">
                        <img
                          src="/assets/img/all-images/location_on.png"
                          alt=""
                        />
                      </div>
                      <div className="input_box">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Add Stop"
                          value={stop.address}
                          onChange={(e) =>
                            updateDropRoundStop(index, e.target.value)
                          }
                        />
                        <img
                          src="/assets/img/all-images/close-icon.png"
                          alt=""
                          style={{ cursor: "pointer" }}
                          onClick={() => removeDropRoundStop(index)}
                        />
                        {activeField == `dropRoundStop-${index}` &&
                          suggestions.length > 0 && (
                            <ul className="autocomplete-suggestions">
                              {suggestions.map((s) => (
                                <li
                                  key={s.place_id}
                                  className="autocomplete-suggestion-item"
                                  onClick={() =>
                                    handlePlaceSelect(
                                      s,
                                      `dropRoundStop-${index}`,
                                    )
                                  }
                                >
                                  <PlaceSuggestionLabel suggestion={s} />
                                </li>
                              ))}
                            </ul>
                          )}
                      </div>
                    </div>
                  ))}
                  <div className="form-group">
                    <div className="input_image">
                      <img
                        src="/assets/img/all-images/location_on.png"
                        alt=""
                      />
                    </div>
                    <div className="input_box">
                      <input
                        type="text"
                        className={`form-control${formErrors.dropRoundDropoff ? " is-invalid" : ""}`}
                        placeholder="Drop-off"
                        value={dropRoundDropoff.address}
                        ref={dropRoundDropoffInputRef}
                        onChange={(e) => {
                          if (formErrors.dropRoundDropoff) {
                            setFormErrors((prev) => ({
                              ...prev,
                              dropRoundDropoff: "",
                            }));
                          }
                          setDropRoundDropoff({
                            address: e.target.value,
                            lat: null,
                            lng: null,
                          });
                          setFareApiError("");
                          setActiveField("dropRoundDropoff");
                          fetchSuggestions(e.target.value);
                        }}
                      />
                      {dropRoundDropoff.address && (
                        <img
                          src="/assets/img/all-images/close-icon.png"
                          alt=""
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setDropRoundDropoff({
                              address: "",
                              lat: null,
                              lng: null,
                            })
                          }
                        />
                      )}
                      {activeField == "dropRoundDropoff" &&
                        suggestions.length > 0 && (
                          <ul className="autocomplete-suggestions">
                            {suggestions.map((s) => (
                              <li
                                key={s.place_id}
                                className="autocomplete-suggestion-item"
                                onClick={() =>
                                  handlePlaceSelect(s, "dropRoundDropoff")
                                }
                              >
                                <PlaceSuggestionLabel suggestion={s} />
                              </li>
                            ))}
                          </ul>
                        )}
                      {formErrors.dropRoundDropoff ? (
                        <div className="invalid-feedback d-block">
                          {formErrors.dropRoundDropoff}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              <div className="form_col">
                <div className="form_title">
                  <h3>Schedule </h3>
                </div>
                <div className="input_col input_col2">
                  <div className="form-group">
                    <div className="input_image">
                      <img
                        src="/assets/img/all-images/Calendar-icon.png"
                        alt=""
                      />
                    </div>
                    <div className="input_box">
                      <input
                        type="text"
                        className={`form-control${formErrors.dropRoundSchedule ? " is-invalid" : ""}`}
                        placeholder="Date and Time"
                        value={dropRoundSchedule}
                        ref={dropRoundScheduleInputRef}
                        onChange={(e) => {
                          if (formErrors.dropRoundSchedule) {
                            setFormErrors((prev) => ({
                              ...prev,
                              dropRoundSchedule: "",
                            }));
                          }
                          setDropRoundSchedule(e.target.value);
                        }}
                        onClick={() =>
                          onOpenDatePicker(
                            setDropRoundSchedule,
                            { minDateStr: dropSchedule },
                            dropRoundSchedule,
                          )
                        }
                        data-bs-toggle="modal"
                        data-bs-target="#dateSelectModal"
                        readOnly
                        style={{ cursor: "pointer" }}
                      />
                      {formErrors.dropRoundSchedule ? (
                        <div className="invalid-feedback d-block">
                          {formErrors.dropRoundSchedule}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              <div className="form_col">
                <div className="form_title">
                  <h3>Flight No. </h3>
                </div>
                <div className="input_col input_col2">
                  <div className="form-group">
                    <div className="input_image">
                      <img src="/assets/img/all-images/plane-icon.png" alt="" />
                    </div>
                    <div className="input_box">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Flight No. (optional)"
                        value={dropRoundFlightNo}
                        onChange={(e) => setDropRoundFlightNo(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

        </div>
      </div>

      {/* ---- Pick-up From Airport ---- */}
      <div style={{ display: activeSubTab == "pickup" ? "block" : "none" }}>
        <div className="transport_colMain">
          <VehicleSelector
            selected={pickVehicle}
            onSelect={setPickVehicle}
            disableAutoSelect={Boolean(initialData)}
          />
          <div className="form_col">
            <div className="form_title">
              <h3>Location </h3>
              <a
                href="#!"
                className="info_modal"
                onClick={(e) => {
                  e.preventDefault();
                  addPickStop();
                }}
              >
                + Add Stop
              </a>
            </div>
            <div className="input_col">
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/plane-icon.png" alt="" />
                </div>
                <div className="input_box">
                  <select
                    className={`form-control${
                      formErrors.pickAirport || pickAirportServiceAreaError
                        ? " is-invalid"
                        : ""
                    }`}
                    value={pickAirport?.id || ""}
                    ref={pickAirportSelectRef}
                    onChange={(e) => {
                      if (formErrors.pickAirport) {
                        setFormErrors((prev) => ({ ...prev, pickAirport: "" }));
                      }
                      const airport = airports.find(
                        (a) => String(a.id) == String(e.target.value),
                      );
                      setPickAirport(airport);
                      setPickTerminalId(
                        defaultTerminalIdForAirport(airport, "pickup"),
                      );
                      setPickAirportServiceAreaError("");
                    }}
                  >
                    <option value="" disabled>
                      Select Airport
                    </option>
                    {airports.map((airport) => (
                      <option key={airport.id} value={airport.id}>
                        {airport.name}
                      </option>
                    ))}
                  </select>
                  {terminalsForDirection(pickAirport, "pickup").length > 0 ? (
                    <div className="t2">
                      {terminalsForDirection(pickAirport, "pickup").map(
                        (terminal) => (
                          <span
                            key={terminal.id}
                            className={
                              String(pickTerminalId) == String(terminal.id)
                                ? "active"
                                : ""
                            }
                            onClick={() => setPickTerminalId(terminal.id)}
                            style={{ cursor: "pointer" }}
                          >
                            {terminal.name}
                          </span>
                        ),
                      )}
                    </div>
                  ) : null}
                  {formErrors.pickAirport || pickAirportServiceAreaError ? (
                    <div className="text-danger small mt-1 invalid-feedback d-block">
                      {formErrors.pickAirport || pickAirportServiceAreaError}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/location_on.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className={`form-control${formErrors.pickDropoff ? " is-invalid" : ""}`}
                    placeholder="Drop-off"
                    value={pickDropoff1.address}
                    ref={pickDropoffInputRef}
                    onChange={(e) => {
                      if (formErrors.pickDropoff) {
                        setFormErrors((prev) => ({ ...prev, pickDropoff: "" }));
                      }
                      setPickDropoff1({
                        address: e.target.value,
                        lat: null,
                        lng: null,
                      });
                      setFareApiError("");
                      setActiveField("pickDropoff1");
                      fetchSuggestions(e.target.value);
                    }}
                  />
                  {pickDropoff1.address && (
                    <img
                      src="/assets/img/all-images/close-icon.png"
                      alt=""
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setPickDropoff1({ address: "", lat: null, lng: null })
                      }
                    />
                  )}
                  {activeField == "pickDropoff1" && suggestions.length > 0 && (
                    <ul className="autocomplete-suggestions">
                      {suggestions.map((s) => (
                        <li
                          key={s.place_id}
                          className="autocomplete-suggestion-item"
                          onClick={() => handlePlaceSelect(s, "pickDropoff1")}
                        >
                          <PlaceSuggestionLabel suggestion={s} />
                        </li>
                      ))}
                    </ul>
                  )}
                  {formErrors.pickDropoff ? (
                    <div className="invalid-feedback d-block">
                      {formErrors.pickDropoff}
                    </div>
                  ) : null}
                </div>
              </div>
              {pickStops.map((stop, index) => (
                <div className="form-group" key={index}>
                  <div className="input_image">
                    <img src="/assets/img/all-images/location_on.png" alt="" />
                  </div>
                  <div className="input_box">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Add Stop"
                      value={stop.address}
                      onChange={(e) => updatePickStop(index, e.target.value)}
                    />
                    <img
                      src="/assets/img/all-images/close-icon.png"
                      alt=""
                      style={{ cursor: "pointer" }}
                      onClick={() => removePickStop(index)}
                    />
                    {activeField == `pickStop-${index}` &&
                      suggestions.length > 0 && (
                        <ul className="autocomplete-suggestions">
                          {suggestions.map((s) => (
                            <li
                              key={s.place_id}
                              className="autocomplete-suggestion-item"
                              onClick={() =>
                                handlePlaceSelect(s, `pickStop-${index}`)
                              }
                            >
                              <PlaceSuggestionLabel suggestion={s} />
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="form_col">
            <div className="form_title">
              <h3>Schedule </h3>
            </div>
            <div className="input_col input_col2">
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/Calendar-icon.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className={`form-control${formErrors.pickSchedule ? " is-invalid" : ""}`}
                    placeholder="Date and Time"
                    value={pickSchedule}
                    ref={pickScheduleInputRef}
                    onChange={(e) => {
                      if (formErrors.pickSchedule) {
                        setFormErrors((prev) => ({ ...prev, pickSchedule: "" }));
                      }
                      setPickSchedule(e.target.value);
                    }}
                    onClick={() =>
                      onOpenDatePicker(setPickSchedule, {}, pickSchedule)
                    }
                    data-bs-toggle="modal"
                    data-bs-target="#dateSelectModal"
                    readOnly
                    style={{ cursor: "pointer" }}
                  />
                  {formErrors.pickSchedule ? (
                    <div className="invalid-feedback d-block">
                      {formErrors.pickSchedule}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="form_col">
            <div className="form_title">
              <h3>Flight No. </h3>
              <a
                href="#!"
                className="info_modal"
                data-bs-toggle="modal"
                data-bs-target="#infoModal"
                onClick={(e) => e.preventDefault()}
              >
                More Info
              </a>
            </div>
            <div className="input_col input_col2">
              <div className="form-group">
                <div className="input_image">
                  <img src="/assets/img/all-images/plane-icon.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Flight No. (optional)"
                    value={pickFlightNo}
                    onChange={(e) => setPickFlightNo(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="form_col">
            <div className="form_title">
              <h3>Guest Info</h3>
            </div>
            <div className="input_col input_col2">
              <div className="form-group form-groupQuantity">
                <QuantityControl
                  label="Passengers"
                  value={pickPassengers}
                  onChange={setPickPassengers}
                  min={1}
                  max={pickLimits.maxPassengers}
                />
                <QuantityControl
                  label="Suitcase"
                  value={pickSuitcase}
                  onChange={setPickSuitcase}
                  min={0}
                  max={pickLimits.maxSuitcase}
                />
              </div>
            </div>
          </div>

          {/* Round Trip Toggle */}
          {/* HIDDEN TEMPORARILY - Round Trip button for Pick from Airport
          {!showPickRoundTrip && (
            <div className="form_col">
              <div className="input_col input_col2">
                <div className="round_trip text-end">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowPickRoundTrip(true)}
                  >
                    Round Trip
                  </button>
                </div>
              </div>
            </div>
          )}
          */}

          {showPickRoundTrip && (
            <>
              <div className="form_col">
                <div className="input_col input_col2">
                  <div className="form_title round_trip text-end">
                    <h3>Round Trip</h3>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setShowPickRoundTrip(false)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
              <div className="form_col">
                <div className="form_title">
                  <h3>Location </h3>
                  <a
                    href="#!"
                    className="info_modal"
                    onClick={(e) => {
                      e.preventDefault();
                      addPickRoundStop();
                    }}
                  >
                    + Add Stop
                  </a>
                </div>
                <div className="input_col">
                  <div className="form-group">
                    <div className="input_image">
                      <img
                        src="/assets/img/all-images/location_on.png"
                        alt=""
                      />
                    </div>
                    <div className="input_box">
                      <input
                        type="text"
                        className={`form-control${formErrors.pickRoundDropoff ? " is-invalid" : ""}`}
                        placeholder="Drop-off"
                        value={pickRoundDropoff.address}
                        ref={pickRoundDropoffInputRef}
                        onChange={(e) => {
                          if (formErrors.pickRoundDropoff) {
                            setFormErrors((prev) => ({
                              ...prev,
                              pickRoundDropoff: "",
                            }));
                          }
                          setPickRoundDropoff({
                            address: e.target.value,
                            lat: null,
                            lng: null,
                          });
                          setFareApiError("");
                          setActiveField("pickRoundDropoff");
                          fetchSuggestions(e.target.value);
                        }}
                      />
                      {pickRoundDropoff.address && (
                        <img
                          src="/assets/img/all-images/close-icon.png"
                          alt=""
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setPickRoundDropoff({
                              address: "",
                              lat: null,
                              lng: null,
                            })
                          }
                        />
                      )}
                      {activeField == "pickRoundDropoff" &&
                        suggestions.length > 0 && (
                          <ul className="autocomplete-suggestions">
                            {suggestions.map((s) => (
                              <li
                                key={s.place_id}
                                className="autocomplete-suggestion-item"
                                onClick={() =>
                                  handlePlaceSelect(s, "pickRoundDropoff")
                                }
                              >
                                <PlaceSuggestionLabel suggestion={s} />
                              </li>
                            ))}
                          </ul>
                        )}
                      {formErrors.pickRoundDropoff ? (
                        <div className="invalid-feedback d-block">
                          {formErrors.pickRoundDropoff}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {pickRoundStops.map((stop, index) => (
                    <div className="form-group" key={index}>
                      <div className="input_image">
                        <img
                          src="/assets/img/all-images/location_on.png"
                          alt=""
                        />
                      </div>
                      <div className="input_box">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Add Stop"
                          value={stop.address}
                          onChange={(e) =>
                            updatePickRoundStop(index, e.target.value)
                          }
                        />
                        <img
                          src="/assets/img/all-images/close-icon.png"
                          alt=""
                          style={{ cursor: "pointer" }}
                          onClick={() => removePickRoundStop(index)}
                        />
                        {activeField == `pickRoundStop-${index}` &&
                          suggestions.length > 0 && (
                            <ul className="autocomplete-suggestions">
                              {suggestions.map((s) => (
                                <li
                                  key={s.place_id}
                                  className="autocomplete-suggestion-item"
                                  onClick={() =>
                                    handlePlaceSelect(
                                      s,
                                      `pickRoundStop-${index}`,
                                    )
                                  }
                                >
                                  <PlaceSuggestionLabel suggestion={s} />
                                </li>
                              ))}
                            </ul>
                          )}
                      </div>
                    </div>
                  ))}
                  <div className="form-group">
                    <div className="input_image">
                      <img src="/assets/img/all-images/plane-icon.png" alt="" />
                    </div>
                    <div className="input_box">
                      <select
                        className={`form-control${formErrors.pickRoundAirport ? " is-invalid" : ""}`}
                        value={pickRoundAirport?.id || ""}
                        ref={pickRoundAirportSelectRef}
                        onChange={(e) => {
                          if (formErrors.pickRoundAirport) {
                            setFormErrors((prev) => ({
                              ...prev,
                              pickRoundAirport: "",
                            }));
                          }
                          const airport = airports.find(
                            (a) => String(a.id) == String(e.target.value),
                          );
                          setPickRoundAirport(airport);
                          setPickRoundTerminalId(
                            defaultTerminalIdForAirport(airport, "drop"),
                          );
                        }}
                      >
                        <option value="" disabled>
                          Select Airport
                        </option>
                        {airports.map((airport) => (
                          <option key={airport.id} value={airport.id}>
                            {airport.name}
                          </option>
                        ))}
                      </select>
                      {formErrors.pickRoundAirport ? (
                        <div className="invalid-feedback d-block">
                          {formErrors.pickRoundAirport}
                        </div>
                      ) : null}
                      {terminalsForDirection(pickRoundAirport, "drop")
                        .length > 0 ? (
                        <div className="t2">
                          {terminalsForDirection(
                            pickRoundAirport,
                            "drop",
                          ).map((terminal) => (
                            <span
                              key={terminal.id}
                              className={
                                String(pickRoundTerminalId) ==
                                String(terminal.id)
                                  ? "active"
                                  : ""
                              }
                              onClick={() =>
                                setPickRoundTerminalId(terminal.id)
                              }
                              style={{ cursor: "pointer" }}
                            >
                              {terminal.name}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              <div className="form_col">
                <div className="form_title">
                  <h3>Schedule </h3>
                </div>
                <div className="input_col input_col2">
                  <div className="form-group">
                    <div className="input_image">
                      <img
                        src="/assets/img/all-images/Calendar-icon.png"
                        alt=""
                      />
                    </div>
                    <div className="input_box">
                      <input
                        type="text"
                        className={`form-control${formErrors.pickRoundSchedule ? " is-invalid" : ""}`}
                        placeholder="Date and Time"
                        value={pickRoundSchedule}
                        ref={pickRoundScheduleInputRef}
                        onChange={(e) => {
                          if (formErrors.pickRoundSchedule) {
                            setFormErrors((prev) => ({
                              ...prev,
                              pickRoundSchedule: "",
                            }));
                          }
                          setPickRoundSchedule(e.target.value);
                        }}
                        onClick={() =>
                          onOpenDatePicker(
                            setPickRoundSchedule,
                            { minDateStr: pickSchedule },
                            pickRoundSchedule,
                          )
                        }
                        data-bs-toggle="modal"
                        data-bs-target="#dateSelectModal"
                        readOnly
                        style={{ cursor: "pointer" }}
                      />
                      {formErrors.pickRoundSchedule ? (
                        <div className="invalid-feedback d-block">
                          {formErrors.pickRoundSchedule}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              <div className="form_col">
                <div className="form_title">
                  <h3>Flight No. </h3>
                </div>
                <div className="input_col input_col2">
                  <div className="form-group">
                    <div className="input_image">
                      <img src="/assets/img/all-images/plane-icon.png" alt="" />
                    </div>
                    <div className="input_box">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Flight No. (optional)"
                        value={pickRoundFlightNo}
                        onChange={(e) => setPickRoundFlightNo(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
